import React, { useEffect, useState } from "react"
import {
  Box,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material"
import { Layout } from "src/components/layout"
import { useTranslation } from "react-i18next"
import { useAuth } from "src/providers/LoginProvider"
import { useRecoilState } from "recoil"
import { ApiError, Tag, TagService, UserImportResult, UserService } from "src/openapi"
import { ShowToast } from "../../states/showToast"
import { ShowProgress } from "../../states/showProgress"
import { EMPLOYMENT_TYPE, Genders, SIDE_MENU, SNACKBAR_TYPES, USER_TYPES } from "src/modules/const"
import { FileUploader } from "react-drag-drop-files"
import BreadCrumb from "src/components/atom/BreadCrumb"
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined"
import "./index.scss"
import { Top } from "../top"
import { useParams } from "react-router"
import InputTextField from "src/components/atom/InputTextField"

const PROGRESS_STATE = {
  NOT_COMPLETED: 1,
  COMPLETED: 3,
}

export const EmployeeImport = () => {
  const { t } = useTranslation("translation", { keyPrefix: "employee" })

  const [, setShowProgress] = useRecoilState(ShowProgress)
  const [, setShowToast] = useRecoilState(ShowToast)
  const [importResult, setImportResults] = useState<UserImportResult[] | null>(null)

  const { user } = useAuth()
  const { companyId } = useParams()

  const [progress, setProgress] = useState<number>(PROGRESS_STATE.NOT_COMPLETED)

  const [password, setPassword] = useState<string>("")
  const [csvFile, setCsvFile] = useState<File | null>(null)

  const [tags, setTags] = useState<Tag[]>([])
  useEffect(() => {
    if (user == null) {
      return
    }
    TagService.listTags(user.token, Number(companyId))
      .then((res) => {
        setTags(res)
      })
      .catch(() => {
        setShowProgress(false)
      })
  }, [user, companyId])

  const handleImport = async () => {
    if (user == null || csvFile == null) {
      return
    }
    setShowProgress(true)

    UserService.importUsers(user!.token, Number(companyId), { password, file: csvFile })
      .then((res) => {
        setImportResults(res)
        setShowProgress(false)
        setShowToast({ show: true, text: t("success_import"), type: SNACKBAR_TYPES.SUCCESS })
        setProgress(PROGRESS_STATE.COMPLETED)
        setCsvFile(null)
      })
      .catch((error: ApiError) => {
        setShowProgress(false)
        setShowToast({
          show: true,
          text: `${t("failed_import")}\n${error.body}`,
          type: SNACKBAR_TYPES.ERROR,
        })
      })
  }

  const handleSelected = (file: File) => {
    setProgress(PROGRESS_STATE.NOT_COMPLETED)
    setCsvFile(file)
    let reader = new FileReader()

    reader.readAsDataURL(file)
    reader.onloadend = function () {}.bind(this)
  }

  const idToTag = new Map(tags.map((tag) => [tag.id, tag.tag]))

  const calcAge = (date: string) => {
    let age = new Date().getFullYear() - new Date(date as string).getFullYear()
    return age.toString()
  }

  const calcPeriod = (joinDate: string) => {
    if (joinDate == null) return ""

    let now = new Date()
    let prev = new Date(joinDate as string)

    let months = (now.getFullYear() - prev.getFullYear()) * 12
    months -= prev.getMonth()
    months += now.getMonth()
    months = months <= 0 ? 0 : months

    let y = ""
    let m = ""
    if (months >= 12) {
      y = Math.round(months / 12).toString()
    } else {
      y = "0"
    }
    m = Number(months % 12).toString()
    return `(${y}${t("year")}${m}${t("months")})`
  }

  const getType = (type: string) => {
    let v = ""
    EMPLOYMENT_TYPE.forEach((t) => {
      if (type == t.value) {
        v = t.name
      }
    })
    return v
  }

  const getUserType = (userType: string) => {
    let v = ""
    USER_TYPES.forEach((t) => {
      if (userType === t.value) {
        v = t.name
      }
    })
    return v
  }

  if (!user) {
    return <Top />
  }

  return (
    <Layout menu={SIDE_MENU.EMPLOYEE_LIST}>
      <BreadCrumb
        paths={[
          {
            name: t("employee_list"),
            link: `/company/${companyId}/employee`,
          },
          {
            name: t("import"),
            link: "",
          },
        ]}
      />
      <Box className="employee-import-description">
        <Typography>{t("import_description1")}</Typography>
        <Typography>{t("import_description2")}</Typography>
        <Typography>{t("import_description3")}</Typography>
      </Box>
      <Box className="employee-import-area">
        <Box className="employee-import-label">
          <Box className="employee-import-label-left">
            <Typography sx={{ marginLeft: "10px", fontSize: "14px" }}>
              {t("import_label_left")}
            </Typography>
          </Box>
          <Box className="employee-import-label-right">{t("password")}</Box>
          <Box className="employee-import-label-right">{t("import_label_right")}</Box>
        </Box>
        <Box className="employee-import-main-area">
          <Box className="employee-import-select-area">
            <Box sx={{ display: "flex" }}>
              <FileUploader
                classes="file-select"
                types={["csv"]}
                hoverTitle=" "
                handleChange={(file: File) => handleSelected(file)}
              >
                <Button className="select-button">{t("select_file")}</Button>
              </FileUploader>
              {csvFile == null ? (
                <Typography className="select-description">{t("select_description")}</Typography>
              ) : (
                <Typography className="selected-description">{csvFile.name}</Typography>
              )}
            </Box>
            <Button
              className={
                progress === PROGRESS_STATE.NOT_COMPLETED
                  ? "employee-import-process"
                  : "employee-import-finished"
              }
            >
              {progress == PROGRESS_STATE.NOT_COMPLETED ? t("not_completed") : t("completed")}
            </Button>
          </Box>
          <Box className="employee-import-password-area">
            <InputTextField
              text={password}
              handleText={setPassword}
              inputStyle={{ width: "160px", height: "38px" }}
              placeholder={t("password")}
              password
            />
          </Box>
          <Box className="employee-import-button-area">
            <Button
              className={
                csvFile == null ? "employee-import-button" : "employee-import-button-selected"
              }
              disabled={csvFile == null}
              onClick={handleImport}
            >
              {t("import")}
              <FileDownloadOutlinedIcon />
            </Button>
          </Box>
        </Box>
      </Box>
      {importResult ? (
        <>
          <Box className="employee-import-description">
            <Typography>{t("import_completed")}</Typography>
          </Box>
          <TableContainer
            style={{
              width: "100%",
              height: "calc(100vh - 180px)",
              display: "flex",
              flexDirection: "column",
              marginTop: "20px",
            }}
          >
            <Table className="employee_table">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ width: "6%" }}>{t("row_num")}</TableCell>
                  <TableCell sx={{ width: "6%" }}>{t("ID")}</TableCell>
                  <TableCell sx={{ width: "9%" }}>{t("name_well")}</TableCell>
                  <TableCell sx={{ width: "9%" }}>{t("name_kana")}</TableCell>
                  <TableCell sx={{ width: "6%" }}>{t("gender")}</TableCell>
                  <TableCell sx={{ width: "4%" }}>{t("age")}</TableCell>
                  <TableCell sx={{ width: "16%" }}>{t("email_address")}</TableCell>
                  <TableCell sx={{ width: "8%" }}>{t("employment_type")}</TableCell>
                  <TableCell sx={{ width: "8%" }}>{t("join_company_date")}</TableCell>
                  <TableCell sx={{ width: "10%" }}>{t("tag")}</TableCell>
                  <TableCell sx={{ width: "8%" }}>{t("user_type")}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {importResult.length == 0 ? (
                  <TableRow>
                    <TableCell colSpan={11} sx={{ textAlign: "center" }}>
                      {t("no_data")}
                    </TableCell>
                  </TableRow>
                ) : (
                  importResult.map((row) => {
                    return row.User && row.User.id > 0 ? (
                      <TableRow key={`${row.Index}`}>
                        <TableCell>{row.Index}</TableCell>
                        <TableCell>{row.User.employeeId}</TableCell>
                        <TableCell>{`${row.User.lastName} ${row.User.firstName}`}</TableCell>
                        <TableCell>{`${row.User.lastNameKana} ${row.User.firstNameKana}`}</TableCell>
                        <TableCell>
                          {row.User.gender == Genders.OTHER
                            ? row.User.genderComment
                            : row.User.gender == Genders.MALE
                              ? t("male")
                              : t("female")}
                        </TableCell>
                        <TableCell>
                          {row.User.birthday ? calcAge(row.User.birthday!) : ""}
                        </TableCell>
                        <TableCell>{row.User.email}</TableCell>
                        <TableCell>{getType(row.User.employmentType!)}</TableCell>
                        <TableCell>{`${
                          row.User.joiningDate != null ? row.User.joiningDate?.substring(0, 7) : ""
                        } ${calcPeriod(row.User.joiningDate!)}`}</TableCell>
                        <TableCell>
                          <Box className="tag-label">
                            {(row.User.tagIds?.length || 0) === 0
                              ? ""
                              : row.User.tagIds?.map((id, index) => {
                                  return (
                                    <Box className="tag-box" key={`tag_${index}`}>
                                      {idToTag.get(id)}
                                    </Box>
                                  )
                                })}
                          </Box>
                        </TableCell>
                        <TableCell>{getUserType(row.User.userType!)}</TableCell>
                      </TableRow>
                    ) : (
                      <TableRow key={`${row.Index}`}>
                        <TableCell>{row.Index}</TableCell>
                        <TableCell colSpan={10}>
                          <Typography style={{ color: "red" }}>{row.ErrorReason}</Typography>
                        </TableCell>
                      </TableRow>
                    )
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      ) : (
        <></>
      )}
    </Layout>
  )
}
